import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Select, MenuItem, Button,
  FormControl, InputLabel, Table, TableHead, TableRow,
  TableCell, TableBody, Paper
} from '@mui/material';

// ---- Date + Time Formatter ----
const formatDateTime = (input) => {
  if (!input) return '';
  const d = new Date(input);
  if (isNaN(d)) return input; // fallback if parsing fails

  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();

  let hh = d.getHours();
  const min = String(d.getMinutes()).padStart(2, '0');
  const ampm = hh >= 12 ? 'PM' : 'AM';
  hh = hh % 12;
  hh = hh ? hh : 12; // 0 => 12

  return `${dd}-${mm}-${yyyy} ${hh}:${min} ${ampm}`;
};

// ---- Filter Helper ----
const isWithinDateRange = (date, filterType) => {
  const now = new Date();
  const inputDate = new Date(date);
  if (isNaN(inputDate)) return false;

  if (filterType === 'recent') {
    return (now - inputDate) / (1000 * 60 * 60 * 24) <= 7;
  }

  if (filterType === 'this_month') {
    return inputDate.getMonth() === now.getMonth() &&
           inputDate.getFullYear() === now.getFullYear();
  }

  if (filterType === 'last_month') {
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return inputDate.getMonth() === lastMonth.getMonth() &&
           inputDate.getFullYear() === lastMonth.getFullYear();
  }

  return true; // 'all'
};

const CoachingHistory = () => {
  const [allPayments, setAllPayments] = useState([]);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const res = await axios.get(
        'get_coaching_payment_history.php'
      );
      setAllPayments(res.data || []);
    } catch (err) {
      console.error('Error fetching payment data:', err);
      setAllPayments([]);
    }
  };

  const filteredPayments = allPayments.filter(payment =>
    isWithinDateRange(payment.date, filterType)
  );

  const totalCollected = filteredPayments.reduce(
    (sum, p) => sum + (parseFloat(p.amount) || 0),
    0
  );

  const handlePrint = () => {
  const printContents = `
    <html>
      <head>
        <title>Coaching Payment History</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; }
          .print-box {
            border: 2px solid #000;
            padding: 24px 32px;
            margin: 26px auto;
            width: 90%;
            background: #fff;
          }
          .school-logo {
            display: block;
            margin: 0 auto 8px auto;
            width: 75px;
            height: 75px;
            object-fit: contain;
          }
          .school-title {
            text-align: center;
            font-size: 22px;
            font-weight: bold;
            margin-bottom: 2px;
            letter-spacing: 1px;
          }
          .school-address {
            text-align: center;
            font-size: 15px;
            color: #555;
            margin-bottom: 10px;
          }
          .print-heading {
            text-align: center;
            font-size: 17px;
            font-weight: 600;
            margin: 18px 0 18px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 6px;
            margin-bottom: 14px;
          }
          th, td {
            border: 1px solid #222;
            padding: 8px 6px;
            font-size: 15px;
            text-align: left;
          }
          th {
            background: #f0f0f0;
            font-weight: bold;
            text-align: center;
          }
          tr:last-child td {
            font-weight: bold;
            background: #FAFAD2;
          }
        </style>
      </head>
      <body>
        <div class="print-box">
          <img src="/school-logo.jpg" alt="School Logo" class="school-logo"
               onload="window.print(); window.close();" />
          <div class="school-title">OXFORD CONVENT ENGLISH MEDIUM SCHOOL, KHAJURI</div>
          <div class="school-address">Khajuri - 585 314, Tq. Aland, Dist. Kalaburagi</div>
          <div class="print-heading">Coaching Payment History</div>
          ${document.getElementById('print-section').innerHTML}
        </div>
      </body>
    </html>
  `;
  const newWindow = window.open('', '', 'width=1000,height=800');
  newWindow.document.write(printContents);
  newWindow.document.close();
};


  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Coaching Payment History
      </Typography>

      <FormControl sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel>Filter</InputLabel>
        <Select
          value={filterType}
          label="Filter"
          onChange={(e) => setFilterType(e.target.value)}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="recent">Recently</MenuItem>
          <MenuItem value="this_month">This Month</MenuItem>
          <MenuItem value="last_month">Last Month</MenuItem>
        </Select>
      </FormControl>

      <Button onClick={handlePrint} variant="contained" color="primary" sx={{ ml: 2, mb: 2 }}>
        Print
      </Button>

      <div id="print-section">
        <Paper elevation={3} sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Transaction Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.map((payment, index) => (
                <TableRow key={index}>
                  <TableCell>{formatDateTime(payment.date)}</TableCell>
                  <TableCell>{payment.name}</TableCell>
                  <TableCell>{payment.class}</TableCell>
                  <TableCell>₹{Number(payment.amount || 0).toFixed(2)}</TableCell>
                  <TableCell>{payment.transaction_type}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3}><b>Total</b></TableCell>
                <TableCell><b>₹{totalCollected.toFixed(2)}</b></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </div>
    </Box>
  );
};

export default CoachingHistory;
